import PropTypes, { bool, func } from "prop-types";

import styled from "styled-components/macro";

import FnrButtonWrapper from "UI/FnrButtonWrapper";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrFade from "UI/FnrFade";
import FnrTypography from "UI/FnrTypography";
import { colors } from "UI/colors";

const preventDefaultMouseDown = (e) => e.preventDefault();

/**
 * The base-retain co-display (§2 "show BOTH"): the save canonicalized the
 * recognized base AND settled the address unverifiable, so both the
 * standardization-undo and the "couldn't verify" notices are true at once.
 * Rather than stack two separate pills, this merges them into ONE container so
 * the pair reads as a single moment. It wears the STANDARDIZATION (blue)
 * palette for the chrome, wand icon, and undo — a single pill palette keeps it
 * from reading as two glued-together signals — while the unverifiable
 * map-marker keeps its original gray, the one nod back to that solo callout.
 *
 * Two rows inside the one pill: the standardization line (wand + heading + undo control) above
 * the unverifiable line (map-marker + two-line copy). Undo is still the only control — accepting
 * is silent (see StandardizationNotice). Does no positioning of its own; the surface places it.
 */
export default function CombinedCallout({
  open,
  onUndo,
  loading,
  flush,
  bare,
  align,
  minHeight,
}) {
  return (
    // inertWhileAnimating: keeps the undo button from flicking :hover as the pill
    // slides under the cursor on reveal — see FnrFade.
    <FnrFade show={open} inertWhileAnimating>
      <Wrapper
        role="region"
        aria-label="address-standardized-unverifiable"
        $flush={flush}
        $bare={bare}
        $align={align}
        $minHeight={minHeight}
      >
        <Row>
          <FnrContainedIcon
            icon="wand-magic-sparkles fas"
            size="sm"
            noBackgroundColor
            color="fnBlue"
          />
          <Text>
            <FnrTypography
              type="copy"
              fontSize="sm"
              color="textSecondaryDark"
              lineHeight={1.2}
            >
              We standardized your address formatting.
            </FnrTypography>
          </Text>
          <UndoButton
            type="button"
            onClick={onUndo}
            onMouseDown={preventDefaultMouseDown}
            aria-label="undo address standardization"
            disabled={loading}
            aria-busy={loading || undefined}
          >
            <FnrContainedIcon
              icon="undo fas"
              size="sm"
              noBackgroundColor
              noPadding
              color="currentColor"
              isLoading={loading}
            />
          </UndoButton>
        </Row>
        <Row>
          <FnrContainedIcon
            icon="map-marker-question fas"
            size="sm"
            noBackgroundColor
            color="textSecondary"
          />
          <Text>
            <FnrTypography
              type="copy"
              fontSize="sm"
              color="textSecondaryDark"
              lineHeight={1.2}
            >
              We couldn&apos;t verify this address
            </FnrTypography>
            <FnrTypography
              type="copy"
              fontSize="xs"
              color="textSecondaryLight"
              lineHeight={1.2}
            >
              If you know it&apos;s correct, you&apos;re all set.
            </FnrTypography>
          </Text>
        </Row>
      </Wrapper>
    </FnrFade>
  );
}

CombinedCallout.propTypes = {
  open: bool,
  // Re-persist what the user saw (their own spelling), marked unverifiable.
  onUndo: func,
  // While the undo POST is in flight: spinner on the undo icon, button disabled.
  loading: bool,
  // Drops the inline (below-row) margin so the pill sits flush as standalone
  // content (e.g. inside the reports popover).
  flush: bool,
  // Strips the pill chrome (background + border) so the content floats on the
  // parent surface — the reports popover supplies the blue background itself.
  bare: bool,
  // Anything other than "left" (incl. unset) centers; inline surfaces left-align, the boards popover centers.
  align: PropTypes.oneOf(["left", "center"]),
  // Override the pill's min-height (px) to match a surrounding corner radius.
  minHeight: PropTypes.number,
};

const NEST_GAP = 2;
const BUTTON_SIZE = 28;

// One blue rounded container holding both rows. A moderate corner radius (not
// the siblings' stadium 999px) reads as a single multi-line card rather than a
// pill stretched tall. Goes bare (no background/border) when the surface paints
// its own background — the reports popover, which sets the same blue itself.
const Wrapper = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: fit-content;
  max-width: 100%;
  margin-top: ${({ $flush }) => ($flush ? "0px" : "8px")};
  margin-bottom: ${({ $flush }) => ($flush ? "0px" : "8px")};
  margin-left: ${({ $align }) => ($align === "left" ? "0" : "auto")};
  margin-right: auto;
  /* Top and right both use NEST_GAP so the undo circle tucks into the corner
     evenly (3px from top = 3px from right); 8px text inset on the bottom and
     left. The standardization row IS the solo callout, with the verify line
     simply stacked below it. */
  padding: ${NEST_GAP}px ${NEST_GAP}px 8px 8px;
  min-height: ${({ $minHeight }) =>
    $minHeight != null ? `${$minHeight}px` : "auto"};
  border: 1px solid ${({ $bare }) => ($bare ? "transparent" : colors.fnBlue200)};
  border-radius: 16px;
  background: ${({ $bare }) => ($bare ? "transparent" : colors.fnBlue100)};
`;

// Each message line: icon + flexing text (+ the undo control on the standardization row);
// align-items center vertically centers the icon against its one- or two-line text block.
const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Flex column blockifies the typography so its own lineHeight governs the line
// boxes (a bare inline would ride this div's 16px strut instead).
const Text = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// Blue icon button, the standalone StandardizationNotice UndoButton's twin:
// bare blue glyph that fills blue on hover. Same size + active scale + optical
// nudge as its siblings.
const UndoButton = styled(FnrButtonWrapper)`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: ${BUTTON_SIZE}px;
  height: ${BUTTON_SIZE}px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${colors.fnBlue};
  transition: background-color 300ms ease, transform 100ms;
  &:hover,
  &:focus-visible {
    background: ${colors.fnBlue};
    color: ${colors.white};
  }
  &:active {
    transform: scale(0.92);
  }
  /* Optical centering for the fa-undo ink once the circle fills — same nudge and rationale as
     StandardizationNotice's UndoButton (see the comment there). */
  & .fa-undo {
    transform: translateX(0.5px);
  }
`;
